import { Alert, Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AllergenService } from '../../../../../services/allergen/allergen.service';
import type {
  Allergen,
  IAllergenService as IAllergenServiceInterface,
} from '../../../../../services/allergen/allergen.service.interface';
import { AllergenChipGrid } from '../../../../recipes/components/allergen-chip-grid';
import { allergenQueryKeys } from '../../../../recipes/query-keys';
import type { ExtractedAllergen } from '../../../hooks/use-item-extraction';
import { useExtractedData } from '../../extracted-data-context';

export type AllergenEntry = {
  allergenId: string;
  allergenCode: string;
  category: 'CONTAINS' | 'MAY_CONTAIN';
};

type AllergenEntryInput = {
  allergenId?: string;
  allergenCode?: string;
  category: 'CONTAINS' | 'MAY_CONTAIN';
};

interface AllergenSelectionStepProps {
  initialAllergens?: AllergenEntryInput[];
  onAllergensChange: (allergens: AllergenEntry[] | null) => void;
  /**
   * AI Act art. 50: true when `initialAllergens` are AI-inferred (not values the
   * operator saved). Photo-extraction prefill is detected inside the component.
   */
  aiPrefilled?: boolean;
}

function buildSetsFromEntries(
  entries: AllergenEntryInput[],
  allergens: Allergen[],
): { containsSet: Set<string>; mayContainSet: Set<string> } {
  const containsSet = new Set<string>();
  const mayContainSet = new Set<string>();
  for (const entry of entries) {
    const match = entry.allergenId
      ? allergens.find((a) => a.id === entry.allergenId)
      : allergens.find((a) => a.code === entry.allergenCode);
    if (match) {
      if (entry.category === 'CONTAINS') {
        containsSet.add(match.id);
      } else {
        mayContainSet.add(match.id);
      }
    }
  }
  return { containsSet, mayContainSet };
}

function setsToEntries(
  containsSet: Set<string>,
  mayContainSet: Set<string>,
  allergens: Allergen[],
): AllergenEntry[] {
  const entries: AllergenEntry[] = [];
  for (const id of containsSet) {
    const allergen = allergens.find((a) => a.id === id);
    if (allergen) {
      entries.push({ allergenId: allergen.id, allergenCode: allergen.code, category: 'CONTAINS' });
    }
  }
  for (const id of mayContainSet) {
    const allergen = allergens.find((a) => a.id === id);
    if (allergen) {
      entries.push({
        allergenId: allergen.id,
        allergenCode: allergen.code,
        category: 'MAY_CONTAIN',
      });
    }
  }
  return entries;
}

export function AllergenSelectionStep({
  initialAllergens,
  onAllergensChange,
  aiPrefilled = false,
}: AllergenSelectionStepProps) {
  const { t } = useTranslation('goodsReceiving');
  const allergenService = useInjection<IAllergenServiceInterface>(AllergenService);

  const { data: allergens = [], isLoading } = useQuery({
    queryKey: allergenQueryKeys.list(),
    queryFn: () => allergenService.findAll(),
    staleTime: 5 * 60 * 1000,
  });

  const [containsSet, setContainsSet] = useState<Set<string>>(new Set());
  const [mayContainSet, setMayContainSet] = useState<Set<string>>(new Set());
  const hasInitialized = useRef(false);

  // Initialize from existing item data (edit mode)
  useEffect(() => {
    if (hasInitialized.current || allergens.length === 0) {
      return;
    }
    if (initialAllergens && initialAllergens.length > 0) {
      const { containsSet: c, mayContainSet: m } = buildSetsFromEntries(
        initialAllergens,
        allergens,
      );
      setContainsSet(c);
      setMayContainSet(m);
      hasInitialized.current = true;
    }
  }, [initialAllergens, allergens]);

  // Auto-populate from extraction when no manual selection has been made
  const { extractedData } = useExtractedData();
  const lastExtractedRef = useRef<ExtractedAllergen[] | null>(null);
  const [extractionPrefilled, setExtractionPrefilled] = useState(false);

  useEffect(() => {
    const extracted = extractedData?.allergens ?? null;
    if (!extracted || extracted.length === 0 || allergens.length === 0) {
      return;
    }
    // Only auto-populate if this is new extraction data (not the same reference)
    if (extracted === lastExtractedRef.current) {
      return;
    }
    lastExtractedRef.current = extracted;

    const { containsSet: c, mayContainSet: m } = buildSetsFromEntries(extracted, allergens);
    setContainsSet(c);
    setMayContainSet(m);
    setExtractionPrefilled(true);
    hasInitialized.current = true;
  }, [extractedData?.allergens, allergens]);

  // Sync selections to parent
  const onAllergensChangeRef = useRef(onAllergensChange);
  onAllergensChangeRef.current = onAllergensChange;

  useEffect(() => {
    if (allergens.length === 0) {
      return;
    }
    if (containsSet.size === 0 && mayContainSet.size === 0) {
      onAllergensChangeRef.current(null);
    } else {
      onAllergensChangeRef.current(setsToEntries(containsSet, mayContainSet, allergens));
    }
  }, [containsSet, mayContainSet, allergens]);

  const handleToggleContains = useCallback((id: string) => {
    setContainsSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    // Remove from mayContain if adding to contains
    setMayContainSet((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleToggleMayContain = useCallback((id: string) => {
    setMayContainSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  if (allergens.length === 0 && !isLoading) {
    return null;
  }

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{
          fontWeight: 'bold',
        }}
      >
        {t('allergensTitle')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t('allergensHelper')}
      </Typography>
      {(aiPrefilled || extractionPrefilled) && (
        <Alert severity="warning" data-ai-generated="true" sx={{ mb: 2 }}>
          {t('aiDisclaimerAllergens')}
        </Alert>
      )}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <AllergenChipGrid
          allergens={allergens}
          selectedContains={containsSet}
          selectedMayContain={mayContainSet}
          onToggleContains={handleToggleContains}
          onToggleMayContain={handleToggleMayContain}
        />
      )}
    </Box>
  );
}
